import React from "react";
import { useTranslation } from "react-i18next";
import { Close, MismatchGradient } from "@dreamhub/designsystem/icons";
import Text from "@/components/ui/text";
import { formatIsoDate } from "@/utils/datetime";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import PopoverWrapper from "../../PopoverWrapper";
import { BaseIndicationProps, IndicationData, resolveAction } from "../../types";
import { usePopoverState, useResolveMismatchMutation } from "../../hooks";
import IconContainer from "@/components/ui/icon-container";
import Badge from "@/components/ui/badge";
import MismatchSection from "@/components/controls/Questions/DreamerIndication/components/MismatchIndication/MismatchSection";
import {
  EntityType,
  getEntityHook
} from "@/services/hooks/entities/useEntityType";
import { getFullName } from "@/entities/people/helpers";
import { PersonData } from "@/entities/people/types";

const TRANSLATION_KEY = "deals.dealView.filledByDreamer.mismatch";

interface Props extends BaseIndicationProps {
  indicationData: IndicationData;
}

const MismatchIndication: React.FC<Props> = ({
  dealId,
  questionId,
  questionResponse,
  size,
  indicationData
}) => {
  const { t } = useTranslation();

  const { isPopoverOpen, setIsPopoverOpen } = usePopoverState();
  const { indicationDate } = indicationData;
  const detectedAt = formatIsoDate(indicationDate);
  const resolveMismatchMutation = useResolveMismatchMutation();

  const handleResolveMismatch = (action: resolveAction) => {
    resolveMismatchMutation.mutate(
      {
        pathParams: {
          dealId,
          questionId
        },
        data: {
          action
        }
      },
      {
        onSuccess: () => {
          setIsPopoverOpen(false);
        }
      }
    );
  };

  const indicationText = t(`${TRANSLATION_KEY}.title`);

  const currentValueId = questionResponse?.response?.value?.toString() || "";
  // Fall back to the candidate id when the backend resolved no name, so the
  // suggestion box is never blank on an otherwise valid mismatch.
  const candidateName =
    questionResponse?.response?.candidateName ||
    questionResponse?.response?.candidateId ||
    "";
  const citation = questionResponse?.response?.citation || "";

  // Fetch person data if it's a person ID
  const usePeopleQuery = getEntityHook(EntityType.PERSON);
  const peopleQuery = usePeopleQuery
    ? usePeopleQuery(currentValueId ? [currentValueId] : [])
    : { entities: [], isSuccess: false };

  const { entities: [personData] = [], isSuccess: isPersonSuccess } =
    peopleQuery;

  const currentValue = isPersonSuccess
    ? getFullName(personData as PersonData)
    : currentValueId;

  const triggerContent = (
    <Badge className="p-[2px] pr-[6px]">
      <div
        className="border-[0.5px] border-border-border2 rounded-lg p-[2px] backdrop-blur-lg"
        style={{
          background:
            "linear-gradient(180deg, var(--opacity-white-16, rgba(255, 255, 255, 0.16)) 0%, var(--opacity-black-24, rgba(12, 17, 29, 0.24)) 100%)"
        }}
      >
        <IconContainer size={size}>
          <MismatchGradient />
        </IconContainer>
      </div>
      <Text variant="bodySecondary">{t(`${TRANSLATION_KEY}.trigger`)}</Text>
    </Badge>
  );

  const popoverContent = (
    <>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1">
          <div className="flex items-center">
            <IconContainer size="lg">
              <MismatchGradient />
            </IconContainer>
          </div>
          <Text variant="lgLabel">{indicationText}</Text>
        </div>
        <div className="flex items-center gap-4">
          {detectedAt ? (
            <Text variant="bodyTertiary">{detectedAt}</Text>
          ) : null}
          <IconButton
            aria-label={t("close")}
            onClick={() => setIsPopoverOpen(false)}
            variant="iconNoBg"
            iconColor="secondary"
            size="md"
          >
            <Close />
          </IconButton>
        </div>
      </div>

      <div className="flex flex-col gap-4">
        <div className="flex gap-2">
          <MismatchSection
            title={t(`${TRANSLATION_KEY}.currentTitle`)}
            sourceLabel={t(`${TRANSLATION_KEY}.dreamerSource`)}
            value={currentValue}
            mode="current"
          />
          <MismatchSection
            title={t(`${TRANSLATION_KEY}.dreamerTitle`)}
            sourceLabel={t(`${TRANSLATION_KEY}.dreamerSource`)}
            value={candidateName}
            mode="dreamer"
            citation={citation}
            footerActions={
              <div className="flex gap-2 justify-end">
                <Button
                  variant="secondary"
                  onClick={() => handleResolveMismatch("dismiss")}
                  disabled={resolveMismatchMutation.isPending}
                >
                  {t("dismiss")}
                </Button>
                <Button
                  variant="primary"
                  onClick={() => handleResolveMismatch("approve")}
                  disabled={resolveMismatchMutation.isPending}
                >
                  {t(`${TRANSLATION_KEY}.accept`)}
                </Button>
              </div>
            }
          />
        </div>
      </div>
    </>
  );

  return (
    <PopoverWrapper
      isPopoverOpen={isPopoverOpen}
      setIsPopoverOpen={setIsPopoverOpen}
      triggerContent={triggerContent}
      tooltipText={indicationText}
      popoverContent={popoverContent}
      useButtonWrapper={false}
      borderClassName="border border-border-border1"
      showBackgroundGradient={false}
      containerClassName="w-[35rem]"
    />
  );
};

export default MismatchIndication;
